import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "../OfficePolicyManagement.module.scss";
import { usePolicyAcknowledgementTemplate } from "repositories/office-policy-management-repository";
import { useTranslation } from "react-i18next";

export default function useMarkAsCompleteStaff({
  officePolicy,
  handleActionOfficePolicy,
  setIsSaveEmployeeModalOpen,
  isModalOpen = true,
}) {
  const { t } = useTranslation();
  const office = officePolicy?.office;
  const ownerId = Array.isArray(office)
    ? office?.[0]?.ownerId
    : office?.ownerId;
  const policyId = officePolicy?.id;
  const policyName = officePolicy?.title;

  const [markAsCompleteState, setMarkAsCompleteState] = useState({
    Initials: "",
    IsAcknowledged: false,
    Acknowledgement: "",
    ApproverId: null,
    Summary: "",
    approvedBy: null,
  });
  const [reReRun, setReRun] = useState({ firstName: false, lastName: false });

  const { data: policyData, isLoading: policyDataLoading } =
    usePolicyAcknowledgementTemplate(ownerId, {
      enabled: !!ownerId && !!isModalOpen,
    });

  const template = policyData?.data ?? policyData;
  const approver = template?.isApprovedByEnabled
    ? officePolicy?.approver
    : null;
  const isPolicyAcknowledgementApprovedByEnabled =
    template?.isApprovedByEnabled &&
    officePolicy?.isPolicyAcknowledgementApprovedByEnabled;
  const isPolicyAcknowledgementEnabled =
    template?.isActive && officePolicy?.isPolicyAcknowledgementEnabled;

  const onHandleChange = useCallback((e) => {
    const { name, value, checked } = e.target;
    setMarkAsCompleteState((prev) => {
      const next = { ...prev };
      if (name === "IsAcknowledged") next.IsAcknowledged = checked;
      else next[name] = value;
      return next;
    });
  }, []);

  const submittedOnceRef = useRef(false);
  const formRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const [errors, setErrors] = useState({
    Initials: "",
    Acknowledgement: "",
    name: "",
  });

  const isInValid = useCallback(() => {
    const prevStates = { ...errors };
    if (isPolicyAcknowledgementEnabled) {
      prevStates.Initials = !markAsCompleteState?.Initials?.trim()
        ? t("officePolicyManagement.pleaseEnterInitials")
        : "";
    } else {
      prevStates.Initials = "";
    }
    if (!markAsCompleteState?.IsAcknowledged) {
      prevStates.Acknowledgement = t(
        "officePolicyManagement.mustAcknowledgePolicyToContinue",
      );
    } else {
      const unfilledFirstName = !firstNameRef?.current?.value?.trim();
      const unfilledLastName = !lastNameRef?.current?.value?.trim();
      prevStates.Acknowledgement = "";
      if (unfilledFirstName && unfilledLastName)
        prevStates.name = t("officePolicyManagement.pleaseEnterName");
      else if (unfilledFirstName)
        prevStates.name = t("officePolicyManagement.pleaseEnterFirstName");
      else if (unfilledLastName)
        prevStates.name = t("officePolicyManagement.pleaseEnterLastName");
      else prevStates.name = "";
    }
    setErrors(prevStates);
    return Object.values(prevStates).some((r) => r !== "");
  }, [markAsCompleteState, errors, isPolicyAcknowledgementEnabled, t]);

  useEffect(() => {
    if (submittedOnceRef.current) isInValid();
  }, [markAsCompleteState, reReRun, isInValid]);

  const inputChangeHandler = useCallback((e) => {
    const { name, value } = e.target;
    setReRun((prev) => ({ ...prev, [name]: value.trim().length > 0 }));
  }, []);

  const renderWithInputs = (text, needToReplaceText) => {
    const regex = /<([^>]+)>/g;
    const elements = [];
    let lastIndex = 0;
    let match;
    let confirmedText = "";
    while ((match = regex.exec(text)) !== null) {
      const matchStart = match.index;
      const matchEnd = regex.lastIndex;
      const placeholder = match[1];
      if (matchStart > lastIndex) {
        const slicedText = text.slice(lastIndex, matchStart);
        if (needToReplaceText) confirmedText += slicedText;
        else elements.push(slicedText);
      }
      if (placeholder === "First Name/Last Name") {
        if (needToReplaceText) {
          confirmedText +=
            (firstNameRef.current?.value?.trim() || "") +
            " " +
            (lastNameRef.current?.value?.trim() || "");
        } else {
          elements.push(
            <input
              key="input-firstname"
              type="text"
              className={styles["input-names"]}
              name="firstName"
              placeholder="Enter First name"
              ref={firstNameRef}
              onChange={inputChangeHandler}
            />,
            <input
              key="input-lastname"
              type="text"
              name="lastName"
              className={styles["input-names"]}
              placeholder="Enter Last Name"
              ref={lastNameRef}
              onChange={inputChangeHandler}
            />,
            <br key="br" />,
          );
        }
      } else if (placeholder === "Policy Name") {
        if (needToReplaceText) confirmedText += `'${policyName}'`;
        else elements.push(`'${policyName}'`);
      } else {
        elements.push(`<${placeholder}>`);
      }
      lastIndex = matchEnd;
    }
    if (lastIndex < text.length) {
      const slicedText = text.slice(lastIndex);
      if (needToReplaceText) confirmedText += slicedText;
      else elements.push(slicedText);
    }
    return needToReplaceText ? confirmedText : elements;
  };

  const submitCompleteOfficePolicy = async (e) => {
    e.preventDefault();
    submittedOnceRef.current = true;
    if (isInValid()) return;
    const params = {
      Initials: isPolicyAcknowledgementEnabled
        ? (markAsCompleteState.Initials || "").trim()
        : "",
      IsAcknowledged: markAsCompleteState.IsAcknowledged,
      Acknowledgement:
        renderWithInputs(markAsCompleteState.Acknowledgement || "", true) +
        (markAsCompleteState.Summary || ""),
      ApproverId: isPolicyAcknowledgementApprovedByEnabled
        ? approver?.id || null
        : null,
    };
    try {
      await handleActionOfficePolicy(policyId, "Complete", params);
      setIsSaveEmployeeModalOpen();
    } catch (_) {}
  };

  useEffect(() => {
    const templateData = policyData?.data ?? policyData;
    if (templateData) {
      setMarkAsCompleteState((prev) => ({
        ...prev,
        Summary: templateData?.summary || "",
        Acknowledgement: templateData?.acknowledgement || "",
        approvedBy: templateData?.approvedBy,
      }));
    }
  }, [policyData]);

  return {
    markAsCompleteState,
    submitCompleteOfficePolicy,
    renderWithInputs,
    errors,
    policyDataLoading,
    formRef,
    onHandleChange,
    approver,
    isPolicyAcknowledgementApprovedByEnabled,
    isPolicyAcknowledgementEnabled,
  };
}
